import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, CardImg, Col, Container, Row, Input, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import ApiClient from '../BaseUrl/ApiClient';

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

function BlogsList() {
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 8,
    totalDocs: 0,
    totalPages: 0,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState({ search: false });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBlogId, setSelectedBlogId] = useState(null);
  const [selectedBlogTitle, setSelectedBlogTitle] = useState('');

  // Fetch blogs from API
  const fetchBlogs = async (page = 1, limit = pagination.limit, query = searchQuery) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = { page, limit };
      if (query) params.search = query;

      setActiveFilters({ search: !!query });

      const response = await ApiClient().get('/admin/blog', { params });
      const { responseData } = response.data;
      setBlogs(responseData.blogs || []);
      setPagination({
        page: responseData.pagination.page || page,
        limit: responseData.pagination.limit || limit,
        totalDocs: responseData.pagination.totalDocs || 0,
        totalPages: responseData.pagination.totalPages || 0,
      });

      if (responseData.blogs.length === 0 && query) {
        toast.info('No blogs found with current search term');
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to load blogs. Please try again later.');
      setBlogs([]);
      toast.error(err.response?.data?.responseMessage?.[0] || 'Failed to fetch blogs');
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedFetchBlogs = useCallback(
    debounce((page, limit, query) => {
      fetchBlogs(page, limit, query);
    }, 500),
    [pagination.limit]
  );

  // Initial data fetch
  useEffect(() => {
    fetchBlogs();
  }, []);

  // Handle search input change
  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedFetchBlogs(1, pagination.limit, query);
  };

  // Clear search filter
  const clearSearchFilter = () => {
    setSearchQuery('');
    setActiveFilters({ search: false });
    debouncedFetchBlogs(1, pagination.limit, '');
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      fetchBlogs(newPage, pagination.limit, searchQuery);
    }
  };

  // Handle limit change
  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination({ ...pagination, limit: newLimit });
    fetchBlogs(1, newLimit, searchQuery);
  };

  // Handle delete blog click
  const handleDeleteClick = (blogId, blogTitle) => {
    setSelectedBlogId(blogId);
    setSelectedBlogTitle(blogTitle);
    setShowDeleteModal(true);
  };

  // Handle blog deletion
  const handleDelete = async () => {
    try {
      await ApiClient().delete(`/admin/blog/${selectedBlogId}`);
      toast.success('Blog deleted successfully');
      setShowDeleteModal(false);
      fetchBlogs(pagination.page, pagination.limit, searchQuery);
    } catch (err) {
      console.error('Error deleting blog:', err);
      toast.error(err.response?.data?.responseMessage?.[0] || 'Failed to delete blog');
    }
  };

  const handleEdit = (blogId) => {
    window.location.href = `/update-blog/${blogId}`;
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h2 className="card-title mb-0">Blogs</h2>
                  <div className="d-flex align-items-center">
                    <h2 className="card-title mb-0 me-3">
                      Total Blogs:{' '}
                      <span className="counter-value">{pagination.totalDocs}</span>
                    </h2>
                    <Link to="/add-blog" className="btn btn-success">
                      <FontAwesomeIcon icon={faPlus} className="me-2" />
                      Add New Blog
                    </Link>
                  </div>
                </CardHeader>

                <CardBody>
                  <div id="blogList">
                    <Row className="g-3 justify-content-end">
                      <Col md={4}>
                        <FormGroup>
                          <div className="search-box position-relative">
                            <Input
                              type="text"
                              id="search"
                              className="form-control ps-5"
                              placeholder="Search blogs..."
                              value={searchQuery}
                              onChange={handleSearchInputChange}
                            />
                            <FontAwesomeIcon
                              icon={faSearch}
                              className="position-absolute"
                              style={{ top: '10px', left: '15px', color: '#6c757d' }}
                            />
                            <FontAwesomeIcon
                              icon={faTimes}
                              className={`clear-icon position-absolute ${!activeFilters.search ? 'd-none' : ''}`}
                              style={{ top: '12px', right: '10px', cursor: 'pointer' }}
                              onClick={clearSearchFilter}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    {isLoading && blogs.length === 0 ? (
                      <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="mt-2">Loading Blogs...</h5>
                      </div>
                    ) : blogs.length === 0 ? (
                      <div className="text-center py-4">
                        <h5 className="mt-2">No Blogs Found</h5>
                        <p className="text-muted">
                          {activeFilters.search
                            ? 'Try adjusting your search term'
                            : 'Get started by adding your first blog post.'}
                        </p>
                        <Link to="/add-blog" className="btn btn-primary mt-2">
                          Create Your First Blog
                        </Link>
                      </div>
                    ) : (
                      <Row className="mt-3">
                        {blogs.map((blog) => (
                          <Col key={blog.id} md={6} lg={3} className="mb-4">
                            <Card className="h-100 blog-card">
                              <CardImg
                                top
                                src={blog.featuredImage?.url || 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRpXwcZKH0-ZitPCpDGfyYGQerEGTRu2kVBpA&s'}
                                alt={blog.title}
                                style={{ height: '200px', objectFit: 'cover' }}
                              />
                              <CardBody>
                                <h5 className="card-title">{blog.title}</h5>
                                <p className="card-text mb-1 text-truncate d-block" >
                                  {blog.description || "No description available"}
                                </p>


                                <p className='text-muted mb-0'>{new Date(blog.createdAt).toLocaleDateString("en-US", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}</p>

                              </CardBody>
                              <div className="card-footer bg-transparent">
                                <div className="d-flex justify-content-between">
                                  <button
                                    onClick={() => handleEdit(blog.id)}
                                    className="btn btn-outline-primary btn-sm"
                                  >
                                    Edit
                                  </button>


                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDeleteClick(blog.id, blog.title)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    )}

                    <div className="d-flex justify-content-end align-items-center mt-3">
                      <div className="pagination-wrap hstack gap-2">
                        <Button
                          color="light"
                          disabled={pagination.page === 1}
                          onClick={() => handlePageChange(pagination.page - 1)}
                        >
                          Previous
                        </Button>

                        <div className="d-flex gap-1">
                          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (pagination.page <= 3) {
                              pageNum = i + 1;
                            } else if (pagination.page >= pagination.totalPages - 2) {
                              pageNum = pagination.totalPages - 4 + i;
                            } else {
                              pageNum = pagination.page - 2 + i;
                            }

                            return (
                              <Button
                                key={pageNum}
                                color={pagination.page === pageNum ? 'primary' : 'light'}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>

                        <Button
                          color="light"
                          disabled={pagination.page === pagination.totalPages}
                          onClick={() => handlePageChange(pagination.page + 1)}
                        >
                          Next
                        </Button>

                        <div className="form-group mb-0">
                          <select
                            className="form-select"
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{ width: '80px' }}
                          >

                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <div className={`modal fade ${showDeleteModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <ModalHeader>
              <h5 className="modal-title">Delete Blog</h5>
            </ModalHeader>
            <ModalBody>
              <p>Are you sure you want to delete the blog "<strong>{selectedBlogTitle}</strong>"?</p>
              <p className='text-danger'>This action cannot be undone.</p>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button color="danger" onClick={handleDelete}>
                Delete
              </Button>
            </ModalFooter>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default BlogsList;